import { API_BASE_URL } from "../../constants/index"; // Base URL for the API
import HttpInterface from "../../helpers/HttpInterface"; // Helper class for making HTTP requests
import { defaultFetchParam, FetchParam } from "../../Models/FetchParam"; // Fetch parameters model and default values
import { buildAPIUrl } from "../../api/apiHelpers"; // Utility function to build the API URL with query parameters
import { PaginatedResponse } from "../../Models/Pagination";
import { Company } from "../../Models/Company";
import HttpClient from "../../api/httpClient";

export class CompaniesService {
  private httpProxy: HttpInterface; // Instance of the HttpInterface to make HTTP requests
  private httpClient: HttpClient; // Instance of the HttpInterface to make HTTP requests

  constructor() {
    this.httpProxy = new HttpInterface();
    this.httpClient = new HttpClient();
  }

  /**
  /* ---------------------------------------------------------------------------
   * METHOD: all
   * ------------------------------------------------------------------------- */

  /**
   * Fetches all companies from the registry service API.
   * * Supports pagination, sorting, and filtering through query parameters.
   *
   * @param {FetchParam} params The query parameters for the API request. Defaults to `defaultFetchParam`.
   * @returns {Promise<PaginatedResponse<Company>>} A promise that resolves with the paginated list of companies.
   */
  all(
    params: FetchParam = defaultFetchParam,
  ): Promise<PaginatedResponse<Company>> {
    return this.httpClient.fetch<PaginatedResponse<Company>>(
      buildAPIUrl(`${API_BASE_URL}/registry-service/companies`, params),
      { method: "GET" },
    );
  }

  /**
   * Fetches a specific company by its ID from the API.
   *
   * @param id The unique identifier of the company to fetch.
   * @returns A promise that resolves with the API response containing the company details.
   */
  get(id: string): Promise<any> {
    const requestOptions = {
      method: "GET", // HTTP method for the request
    };

    // Constructs the URL for fetching a specific company by its ID
    return this.httpProxy.fetch(
      `${API_BASE_URL}/registry-service/companies/${id}`,
      requestOptions,
    );
  }

  /**
   * Creates a new company in the registry service API.
   *
   * @param company The details of the company to create.
   * @returns A promise that resolves with the API response confirming the creation of the company.
   */
  create(company: any): Promise<any> {
    const requestOptions = {
      method: "POST", // HTTP method for the request
      body: JSON.stringify(company), // Sends the company data as JSON in the request body
    };

    // Sends a POST request to create the new company
    return this.httpProxy.fetch(
      `${API_BASE_URL}/registry-service/companies`,
      requestOptions,
    );
  }

  /**
   * Updates an existing company in the registry service API.
   *
   * @param company The updated details of the company.
   * @returns A promise that resolves with the API response confirming the update.
   */
  edit(company: any): Promise<any> {
    const requestOptions = {
      method: "PUT", // HTTP method for the request
      body: JSON.stringify(company), // Sends the updated company data as JSON in the request body
    };

    // Sends a PUT request to update the specific company by its ID
    return this.httpProxy.fetch(
      `${API_BASE_URL}/registry-service/companies/${company.id}`,
      requestOptions,
    );
  }

  /**
   * Deletes a company from the registry service API.
   *
   * @param id The unique identifier of the company to delete.
   * @returns A promise that resolves with the API response confirming the deletion.
   */
  delete(id: string): Promise<any> {
    const requestOptions = {
      method: "DELETE", // HTTP method for the request
    };

    // Sends a DELETE request to remove the specific company by its ID
    return this.httpProxy.fetch(
      `${API_BASE_URL}/registry-service/companies/${id}`,
      requestOptions,
    );
  }

  /**
   * Retrieves the list of available roles for a specific company.
   *
   * @param {string} id - The unique identifier of the company.
   * @returns {Promise<any>} A promise that resolves with the list of available roles.
   */
  getAvailableRoles(id: string): Promise<any> {
    const requestOptions = {
      method: "GET",
    };

    return this.httpProxy.fetch(
      `${API_BASE_URL}/registry-service/companies/${id}/available-roles`,
      requestOptions,
    );
  }

  /**
   * Fetches generic fields for a specific company by type.
   *
   * Sends a GET request to the registry service to retrieve the generic fields
   * associated with the given company ID and field type.
   *
   * @param {string} id - The unique identifier of the company.
   * @param {string} type - The type of generic field to retrieve (e.g., "billing", "logistics").
   * @returns {Promise<any>} A promise that resolves to the fetched generic field data.
   */
  getGenericFieldsByType(id: string, type: string): Promise<any> {
    const requestOptions = {
      method: "GET",
    };

    return this.httpProxy.fetch(
      `${API_BASE_URL}/registry-service/companies/${id}/generic-fields/${type}`,
      requestOptions,
    );
  }
}
